import { CoreLanguageType } from 'shared/src/types/languages.type';
import { reportButtonTranslations } from './translations/report-button';

/**
 * Copy for the "Report this page" control in both footers
 * (components/report/report-content-button.tsx) and its dialog. English, French,
 * Spanish and German are authored here; every other language lives in
 * translations/<lang>/report-button.ts.
 */

export type ReportReasonKey =
  | 'sexually-explicit'
  | 'inaccurate-or-fabricated'
  | 'regulated-product'
  | 'dangerous-product'
  | 'ip-infringement'
  | 'wrong-product'
  | 'other';

export type ReportButtonLabels = {
  /** The footer button. */
  report: string;
  title: string;
  reasons: Record<ReportReasonKey, string>;
  detail: string;
  submit: string;
  cancel: string;
  sent: string;
  failed: string;
};

const reportButtonContentCore: Partial<Record<CoreLanguageType, ReportButtonLabels>> & {
  en: ReportButtonLabels;
} = {
  // Every other language: translations/<lang>/report-button.ts. Hand-written records below win.
  ...reportButtonTranslations,
  en: {
    report: 'Report this page',
    title: 'Report a problem with this page',
    reasons: {
      'inaccurate-or-fabricated': 'Inaccurate or made-up information',
      'wrong-product': 'Products don’t match the page',
      'sexually-explicit': 'Sexually explicit content',
      'regulated-product': 'Prescription or regulated product',
      'dangerous-product': 'Dangerous product',
      'ip-infringement': 'Infringes my rights (trademark/copyright)',
      other: 'Something else',
    },
    detail: 'Details (optional)',
    submit: 'Send report',
    cancel: 'Cancel',
    sent: 'Thanks. We review reports within 24 hours.',
    failed: 'Could not send the report. Please try again.',
  },
  fr: {
    report: 'Signaler cette page',
    title: 'Signaler un problème sur cette page',
    reasons: {
      'inaccurate-or-fabricated': 'Information inexacte ou inventée',
      'wrong-product': 'Les produits ne correspondent pas à la page',
      'sexually-explicit': 'Contenu sexuellement explicite',
      'regulated-product': 'Produit sur ordonnance ou réglementé',
      'dangerous-product': 'Produit dangereux',
      'ip-infringement': 'Porte atteinte à mes droits (marque/droit d’auteur)',
      other: 'Autre chose',
    },
    detail: 'Détails (facultatif)',
    submit: 'Envoyer le signalement',
    cancel: 'Annuler',
    sent: 'Merci. Nous examinons les signalements sous 24 heures.',
    failed: 'Échec de l’envoi. Veuillez réessayer.',
  },
  es: {
    report: 'Denunciar esta página',
    title: 'Denunciar un problema en esta página',
    reasons: {
      'inaccurate-or-fabricated': 'Información inexacta o inventada',
      'wrong-product': 'Los productos no corresponden a la página',
      'sexually-explicit': 'Contenido sexualmente explícito',
      'regulated-product': 'Producto con receta o regulado',
      'dangerous-product': 'Producto peligroso',
      'ip-infringement': 'Infringe mis derechos (marca/derechos de autor)',
      other: 'Otra cosa',
    },
    detail: 'Detalles (opcional)',
    submit: 'Enviar denuncia',
    cancel: 'Cancelar',
    sent: 'Gracias. Revisamos las denuncias en un plazo de 24 horas.',
    failed: 'No se pudo enviar. Inténtalo de nuevo.',
  },
  de: {
    report: 'Diese Seite melden',
    title: 'Ein Problem mit dieser Seite melden',
    reasons: {
      'inaccurate-or-fabricated': 'Ungenaue oder erfundene Informationen',
      'wrong-product': 'Produkte passen nicht zur Seite',
      'sexually-explicit': 'Sexuell explizite Inhalte',
      'regulated-product': 'Verschreibungspflichtiges/reguliertes Produkt',
      'dangerous-product': 'Gefährliches Produkt',
      'ip-infringement': 'Verletzt meine Rechte (Marke/Urheberrecht)',
      other: 'Etwas anderes',
    },
    detail: 'Details (optional)',
    submit: 'Meldung senden',
    cancel: 'Abbrechen',
    sent: 'Danke. Wir prüfen Meldungen innerhalb von 24 Stunden.',
    failed: 'Senden fehlgeschlagen. Bitte erneut versuchen.',
  },
};

export function getReportButtonLabels(languageCode: string): ReportButtonLabels {
  return (reportButtonContentCore as Record<string, ReportButtonLabels | undefined>)[languageCode] ?? reportButtonContentCore.en;
}
